/**
 * Unified Appointment Result Selectors — Single Source of Truth
 *
 * Every result view (Appointment Results, Command Dashboard,
 * Disposition Report, Rep Scorecards, Commissions) MUST use these selectors.
 *
 * ── Canonical Pricing Rule ──────────────────────────────────────────
 * For SOLD outcomes with a linked Job, the displayed price MUST come from
 * `Job.financials.contractValue` (the canonical, change-order-aware truth).
 * `pricingSnapshot.bestValuePrice` is audit history and is used ONLY as a
 * fallback when no linked Job exists. Use `getDisplayPrice()` everywhere
 * a sold-deal value is rendered or aggregated. See canonicalPricing.test.ts.
 */

import type { AppointmentResult, AppointmentResultType } from "./appointmentResultTypes";
import type { Job } from "./types";
import { calculateCloseRate, calculateCloseRatePercent } from "@/lib/metricsUtils";

/* ═══════ CANONICAL PRICING ═══════ */

/**
 * Build a lookup from AppointmentResult.id → Job, using both backlink fields.
 * `Job.appointmentResultId` is preferred; `Job.opportunityId` is a fallback.
 */
export function buildJobsByResult(jobs: Job[]): Map<string, Job> {
  const byArId = new Map<string, Job>();
  const byOppId = new Map<string, Job>();
  for (const j of jobs) {
    if (j.appointmentResultId) byArId.set(j.appointmentResultId, j);
    if (j.opportunityId) byOppId.set(j.opportunityId, j);
  }
  // Caller passes either map; we expose a combined accessor via getDisplayPrice
  // but return the AR-id map (primary). Use `getDisplayPrice(result, jobs)` for
  // the full resolution chain.
  return byArId;
  // (byOppId is reconstructed inside getDisplayPrice when needed)
}

/**
 * Canonical SOLD-price resolver.
 *   1. SOLD + linked Job (by appointmentResultId or opportunityId) → Job.financials.contractValue
 *   2. SOLD without linked Job → pricingSnapshot.bestValuePrice (audit fallback)
 *   3. Non-SOLD → pricingSnapshot.bestValuePrice (e.g. high-value lost deals)
 *   4. No data → 0
 */
export function getDisplayPrice(result: AppointmentResult, jobs: Job[] | undefined): number {
  if (result.resultType === "sold" && jobs && jobs.length > 0) {
    const resultOpp = (result.opportunityId || "").trim();
    const linked = jobs.find(
      (j) =>
        (j.appointmentResultId && j.appointmentResultId === result.id) ||
        (j.opportunityId && j.opportunityId === resultOpp) ||
        ((j as { convertedFromQuoteId?: string }).convertedFromQuoteId === resultOpp) ||
        j.id === resultOpp ||
        String((j as { jobNumber?: string }).jobNumber ?? "").trim() === resultOpp,
    );
    const cv = linked?.financials?.contractValue;
    if (typeof cv === "number" && cv > 0) return cv;
    if (typeof cv === "string") {
      const parsed = Number(cv);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    const payout = linked?.payoutAmount;
    if (typeof payout === "number" && payout > 0) return payout;
    if (typeof payout === "string") {
      const parsed = Number(payout);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  }
  return result.pricingSnapshot?.bestValuePrice || 0;
}

/* ═══════ RESULT METRICS ═══════ */

export interface ResultMetrics {
  total: number;
  sold: number;
  noSale: number;
  rescheduled: number;
  noDemo: number;
  demosRan: number;
  totalRevenue: number;
  closeRate: number;
  closeRatePercent: number;
  showRate: number;
  noDemoRate: number;
}

/** Compute canonical result metrics from any result set.
 *  Pass `jobs` to resolve SOLD revenue from canonical Job.financials.contractValue. */
export function getResultMetrics(results: AppointmentResult[], jobs?: Job[]): ResultMetrics {
  const total = results.length;
  const sold = results.filter((r) => r.resultType === "sold").length;
  const noSale = results.filter((r) => r.resultType === "no_sale").length;
  const rescheduled = results.filter((r) => r.resultType === "rescheduled").length;
  const noDemo = results.filter((r) => r.resultType === "no_demo").length;
  const demosRan = total - noDemo;
  const totalRevenue = results
    .filter((r) => r.resultType === "sold")
    .reduce((sum, r) => sum + getDisplayPrice(r, jobs), 0);
  const closeRate = calculateCloseRate(results);
  const closeRatePercent = calculateCloseRatePercent(results);
  const showRate = total > 0 ? Math.round((demosRan / total) * 100) : 0;
  const noDemoRate = total > 0 ? Math.round((noDemo / total) * 100) : 0;

  return { total, sold, noSale, rescheduled, noDemo, demosRan, totalRevenue, closeRate, closeRatePercent, showRate, noDemoRate };
}

/* ═══════ SELECTORS ═══════ */

export function getAllAppointmentResults(results: AppointmentResult[]): AppointmentResult[] {
  return results;
}

export function getRepAppointmentResults(results: AppointmentResult[], repName: string): AppointmentResult[] {
  return results.filter((r) => r.assignedRep === repName);
}

export function getResultsByType(results: AppointmentResult[], type: AppointmentResultType): AppointmentResult[] {
  return results.filter((r) => r.resultType === type);
}

export function getResultsBySource(results: AppointmentResult[], source: string): AppointmentResult[] {
  return results.filter((r) => r.source === source);
}

export function getResultsByDateRange(results: AppointmentResult[], start: Date, end: Date): AppointmentResult[] {
  return results.filter((r) => {
    const d = new Date(r.appointmentDatetime);
    return d >= start && d <= end;
  });
}

/** Commission-eligible results (sold deals) */
export function getCommissionEligibleResults(results: AppointmentResult[]): AppointmentResult[] {
  return results.filter((r) => r.resultType === "sold");
}

/** Get disposition metrics grouped by reason */
export function getDispositionMetrics(results: AppointmentResult[]) {
  const noSaleByReason = new Map<string, number>();
  const noDemoByReason = new Map<string, number>();

  for (const r of results) {
    if (r.resultType === "no_sale" && r.noSaleReason) {
      noSaleByReason.set(r.noSaleReason, (noSaleByReason.get(r.noSaleReason) || 0) + 1);
    }
    if (r.resultType === "no_demo" && r.noDemoReason) {
      noDemoByReason.set(r.noDemoReason, (noDemoByReason.get(r.noDemoReason) || 0) + 1);
    }
  }

  return { noSaleByReason, noDemoByReason };
}

/** Revenue leaks: no-sale without follow-up, high-value lost.
 *  `jobs` not used today (no-sale has no linked job), reserved for symmetry. */
export function getRevenueLeaks(
  results: AppointmentResult[],
  followUpTaskOpportunityIds: Set<string>,
  jobs?: Job[],
) {
  const noSaleNoFollowUp = results.filter(
    (r) => r.resultType === "no_sale" && !followUpTaskOpportunityIds.has(r.opportunityId),
  );
  const noDemoNoReschedule = results.filter(
    (r) => r.resultType === "no_demo" && !r.rescheduleDatetime,
  );
  const highValueLost = results
    .filter((r) => r.resultType === "no_sale" && getDisplayPrice(r, jobs) >= 15000)
    .sort((a, b) => getDisplayPrice(b, jobs) - getDisplayPrice(a, jobs));

  return { noSaleNoFollowUp, noDemoNoReschedule, highValueLost };
}

/** Rep performance summary derived from results.
 *  Pass `jobs` so SOLD revenue resolves from canonical Job.financials.contractValue. */
export function getRepPerformanceSummary(results: AppointmentResult[], jobs?: Job[]) {
  const repMap = new Map<string, { rep: string; revenue: number; sold: number; ran: number; total: number; noSale: number; rescheduled: number; noDemo: number }>();

  for (const r of results) {
    const rep = r.assignedRep || "Unknown";
    const cur = repMap.get(rep) || { rep, revenue: 0, sold: 0, ran: 0, total: 0, noSale: 0, rescheduled: 0, noDemo: 0 };
    cur.total++;
    if (r.resultType !== "no_demo") cur.ran++;
    if (r.resultType === "sold") {
      cur.sold++;
      cur.revenue += getDisplayPrice(r, jobs);
    } else if (r.resultType === "no_sale") {
      cur.noSale++;
    } else if (r.resultType === "rescheduled") {
      cur.rescheduled++;
    } else if (r.resultType === "no_demo") {
      cur.noDemo++;
    }
    repMap.set(rep, cur);
  }

  return [...repMap.values()].map((d) => ({
    ...d,
    closeRate: d.ran > 0 ? Math.round((d.sold / d.ran) * 100) : 0,
  })).sort((a, b) => b.revenue - a.revenue);
}

/** Source performance summary derived from results.
 *  Pass `jobs` so SOLD revenue resolves from canonical Job.financials.contractValue. */
export function getSourcePerformanceSummary(results: AppointmentResult[], jobs?: Job[]) {
  const srcMap = new Map<string, { source: string; leads: number; sold: number; revenue: number }>();

  for (const r of results) {
    const src = r.source || "Unknown";
    const cur = srcMap.get(src) || { source: src, leads: 0, sold: 0, revenue: 0 };
    cur.leads++;
    if (r.resultType === "sold") {
      cur.sold++;
      cur.revenue += getDisplayPrice(r, jobs);
    }
    srcMap.set(src, cur);
  }

  return [...srcMap.values()].sort((a, b) => b.revenue - a.revenue);
}

/** Daily trend data for charts.
 *  Pass `jobs` so SOLD revenue resolves from canonical Job.financials.contractValue. */
export function getDailyTrends(results: AppointmentResult[], days: number = 30, jobs?: Job[]) {
  const data: { date: string; revenue: number; closeRate: number; appointments: number }[] = [];

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const dayResults = results.filter((r) => r.submittedAt?.slice(0, 10) === key);
    const sold = dayResults.filter((r) => r.resultType === "sold");
    data.push({
      date: `${d.getMonth() + 1}/${d.getDate()}`,
      revenue: sold.reduce((s, r) => s + getDisplayPrice(r, jobs), 0),
      closeRate: calculateCloseRatePercent(dayResults),
      appointments: dayResults.length,
    });
  }

  return data;
}
